/**
 * Profile hooks — TanStack Query for profile data, stats, heatmap, achievements.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  fetchProfile,
  updateProfile,
  softDeleteProfile,
  fetchActivityHeatmap,
  fetchUserAchievements,
  fetchUserStats,
  blockUser,
  unblockUser,
  fetchBlockedUsers,
} from '@turnifly/shared/api'
import type { UserStatsResult, HeatmapEntry, UserAchievementWithDetails, FollowListItem } from '@turnifly/shared/api'
import { ProfileSchema } from '@turnifly/shared/types'
import type { Profile, ProfileUpdate } from '@turnifly/shared/types'
import { useAuthStore } from '@turnifly/shared/stores'

// ── Query Keys ──────────────────────────────────────────────

export const profileKeys = {
  all: ['profile'] as const,
  detail: (userId: string) => ['profile', userId] as const,
  stats: (userId: string) => ['profile', 'stats', userId] as const,
  heatmap: (userId: string, year: number) => ['profile', 'heatmap', userId, year] as const,
  achievements: (userId: string, year?: number) =>
    ['profile', 'achievements', userId, year] as const,
  blockedUsers: (userId: string) => ['profile', 'blocked', userId] as const,
} as const

// ── Profile Data ────────────────────────────────────────────

export function useProfile(userId: string | undefined): ReturnType<typeof useQuery<Profile>> {
  return useQuery({
    queryKey: profileKeys.detail(userId ?? ''),
    queryFn: () => fetchProfile(userId!),
    enabled: !!userId,
    staleTime: 60_000,
    select: (data) => ProfileSchema.parse(data),
  })
}

// ── Update Profile ──────────────────────────────────────────

export function useUpdateProfile(): ReturnType<typeof useMutation<unknown, Error, { userId: string; updates: ProfileUpdate }>> {
  const queryClient = useQueryClient()
  const { setUser, user } = useAuthStore()

  return useMutation({
    mutationFn: ({ userId, updates }: { userId: string; updates: ProfileUpdate }) =>
      updateProfile(userId, updates as Record<string, unknown>),
    onMutate: async ({ userId, updates }) => {
      await queryClient.cancelQueries({ queryKey: profileKeys.detail(userId) })
      const previous = queryClient.getQueryData(profileKeys.detail(userId))

      // Optimistic update
      queryClient.setQueryData(profileKeys.detail(userId), (old: unknown) => {
        if (!old) return old
        return { ...(old as Record<string, unknown>), ...updates }
      })

      return { previous, userId }
    },
    onSuccess: (data, { userId }) => {
      const parsed = ProfileSchema.safeParse(data)
      if (parsed.success) {
        queryClient.setQueryData(profileKeys.detail(userId), parsed.data)
        if (user?.id === userId) {
          setUser(parsed.data)
        }
      } else {
        // Invalidate so fresh data is fetched from server
        void queryClient.invalidateQueries({ queryKey: profileKeys.detail(userId) })
      }
    },
    onError: (_err, _vars, context) => {
      if (context?.previous && context?.userId) {
        queryClient.setQueryData(profileKeys.detail(context.userId), context.previous)
      }
    },
    onSettled: (_data, _error, { userId }) => {
      void queryClient.invalidateQueries({ queryKey: profileKeys.detail(userId) })
    },
  })
}

// ── Delete Profile (soft) ───────────────────────────────────

export function useDeleteProfile(): ReturnType<typeof useMutation<void, Error, string>> {
  const { logout } = useAuthStore()

  return useMutation({
    mutationFn: (userId: string) => softDeleteProfile(userId),
    onSuccess: () => {
      localStorage.removeItem('turnifly_access_token')
      localStorage.removeItem('turnifly_refresh_token')
      logout()
    },
  })
}

// ── User Stats ──────────────────────────────────────────────

export function useUserStats(userId: string | undefined): ReturnType<typeof useQuery<UserStatsResult>> {
  return useQuery({
    queryKey: profileKeys.stats(userId ?? ''),
    queryFn: () => fetchUserStats(userId!),
    enabled: !!userId,
    staleTime: 60_000,
  })
}

// ── Activity Heatmap ────────────────────────────────────────

export function useActivityHeatmap(
  userId: string | undefined,
  year: number,
): ReturnType<typeof useQuery<HeatmapEntry[]>> {
  return useQuery({
    queryKey: profileKeys.heatmap(userId ?? '', year),
    queryFn: () => fetchActivityHeatmap(userId!, year),
    enabled: !!userId,
    staleTime: 5 * 60_000,
  })
}

// ── Achievements ────────────────────────────────────────────

export function useUserAchievements(
  userId: string | undefined,
  year?: number,
): ReturnType<typeof useQuery<UserAchievementWithDetails[]>> {
  return useQuery({
    queryKey: profileKeys.achievements(userId ?? '', year),
    queryFn: () => fetchUserAchievements(userId!, year),
    enabled: !!userId,
    staleTime: 5 * 60_000,
  })
}

// ── Blocked Users ──────────────────────────────────────────

export function useBlockedUsers(userId: string | undefined): ReturnType<typeof useQuery<FollowListItem[]>> {
  return useQuery({
    queryKey: profileKeys.blockedUsers(userId ?? ''),
    queryFn: () => fetchBlockedUsers(userId!),
    enabled: !!userId,
    staleTime: 60_000,
  })
}

export function useBlockUser(): ReturnType<typeof useMutation<void, Error, { blockerId: string; blockedId: string; isBlocked: boolean }>> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ blockerId, blockedId, isBlocked }) =>
      isBlocked ? unblockUser(blockerId, blockedId) : blockUser(blockerId, blockedId),
    onSuccess: (_data, { blockerId }) => {
      void queryClient.invalidateQueries({ queryKey: profileKeys.blockedUsers(blockerId) })
      void queryClient.invalidateQueries({ queryKey: ['follow'] })
    },
  })
}
